import { InjectionToken } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ClaimValues } from '@mt-ng2/auth-module';

/**
 * Token used for providing the INavSidebarService
 * @example
 * // in the app providers section
 * providers: [
 *     { provide: NavSidebarServiceToken, useExisting: NavSidebarService },
 * ],
 */
const NavSidebarServiceToken = new InjectionToken('NavSidebarService');

/**
 * class meant to be extended for custom nav content header component
 * the header property is assigned when the component is instantiated and
 * will contain the header item that this component was created for
 */
class NavContentDynamicHeaderComponent {
}
/**
 * class meant to be extended for custom nav content row component
 * the row property is assigned when the component is instantiated and
 * will contain the row item that this component was created for
 */
class NavContentDynamicRowComponent {
}
/**
 * class for a section of content in the sidebar
 * @property header - config for the header for this content
 * @property headerComponent - a component to be rendered in place of the standard header
 * @property rows - array of row items to show in this content
 * @property rowComponent - a component to be rendered in place of the standard row item
 */
class NavSidebarContent {
    constructor(content) {
        Object.assign(this, content);
    }
    get headerHasComponent() {
        return this.headerComponent ? true : false;
    }
    get rowHasComponent() {
        return this.rowComponent ? true : false;
    }
}
/**
 * class that holds a content section in an observable subject, so that
 * any changes can be handled by the sidebar component
 */
class NavSidebarContentContainer {
    constructor(content) {
        this.content = new BehaviorSubject(new NavSidebarContent(content));
    }
}
class NavSidebarContentItemBase {
    constructor(contentItemBase) {
        Object.assign(this, contentItemBase);
    }
}
function getNavLinkSafe(navLink) {
    if (typeof navLink === 'string') {
        return { link: navLink };
    }
    return navLink;
}
class NavSidebarRowItem {
    constructor(rowItem) {
        const defaultRowItem = {
            addClaimType: null,
            addClaimValues: null,
            addLink: null,
            claimType: null,
            claimValues: null,
            content: null,
            icon: null,
            link: null,
            rowComponent: null,
            settingsClaimType: null,
            settingsClaimValues: null,
            settingsLink: null,
            shortcut: null,
        };
        rowItem.link = getNavLinkSafe(rowItem.link);
        rowItem.settingsLink = getNavLinkSafe(rowItem.settingsLink);
        rowItem.addLink = getNavLinkSafe(rowItem.addLink);
        Object.assign(this, defaultRowItem, rowItem);
        if (this.claimType && !this.claimValues) {
            this.claimValues = [ClaimValues.ReadOnly, ClaimValues.FullAccess];
        }
        if (this.addClaimType && !this.addClaimValues) {
            this.addClaimValues = [ClaimValues.ReadOnly, ClaimValues.FullAccess];
        }
        if (this.settingsClaimType && !this.settingsClaimValues) {
            this.settingsClaimValues = [ClaimValues.ReadOnly, ClaimValues.FullAccess];
        }
    }
    rowHasComponent(rowComponent) {
        return this.rowComponent || rowComponent ? true : false;
    }
    getRowComponent(rowComponent) {
        return this.rowComponent ? this.rowComponent : rowComponent;
    }
}
class NavSidebarParentRowItem {
    constructor(parentRowItem) {
        const defaultParentRowItem = {
            addClaimType: null,
            addClaimValues: null,
            addLink: null,
            canToggleExpand: true,
            children: [],
            claimType: null,
            claimValues: null,
            content: null,
            expanded: true,
            icon: null,
            link: null,
            rowComponent: null,
            settingsClaimType: null,
            settingsClaimValues: null,
            settingsLink: null,
        };
        parentRowItem.link = getNavLinkSafe(parentRowItem.link);
        parentRowItem.settingsLink = getNavLinkSafe(parentRowItem.settingsLink);
        parentRowItem.addLink = getNavLinkSafe(parentRowItem.addLink);
        Object.assign(this, defaultParentRowItem, parentRowItem);
        if (this.claimType && !this.claimValues) {
            this.claimValues = [ClaimValues.ReadOnly, ClaimValues.FullAccess];
        }
        if (this.addClaimType && !this.addClaimValues) {
            this.addClaimValues = [ClaimValues.ReadOnly, ClaimValues.FullAccess];
        }
        if (this.settingsClaimType && !this.settingsClaimValues) {
            this.settingsClaimValues = [ClaimValues.ReadOnly, ClaimValues.FullAccess];
        }
    }
    rowHasComponent(rowComponent) {
        return this.rowComponent || rowComponent ? true : false;
    }
    getRowComponent(rowComponent) {
        return this.rowComponent ? this.rowComponent : rowComponent;
    }
}
class NavSidebarHeaderItem {
    constructor(headerItem) {
        const defaultHeaderItem = {
            canToggleExpand: false,
            claimType: null,
            claimValues: null,
            content: null,
            expanded: true,
        };
        Object.assign(this, defaultHeaderItem, headerItem);
        if (this.claimType && !this.claimValues) {
            this.claimValues = [ClaimValues.ReadOnly, ClaimValues.FullAccess];
        }
    }
}

/**
 * Token used for providing the INavModuleConfig
 * @example
 * // in the app providers section
 * providers: [
 *     { provide: NavModuleConfigToken, useValue: navModuleConfig },
 * ],
 */
const NavModuleConfigToken = new InjectionToken('nav_module_config');

/**
 * Generated bundle index. Do not edit.
 */

export { NavContentDynamicHeaderComponent, NavContentDynamicRowComponent, NavModuleConfigToken, NavSidebarContent, NavSidebarContentContainer, NavSidebarContentItemBase, NavSidebarHeaderItem, NavSidebarParentRowItem, NavSidebarRowItem, NavSidebarServiceToken, getNavLinkSafe };

//# sourceMappingURL=mt-ng2-nav-module-config.js.map